/* reflect-metadata has to be imported first */
/* eslint-disable import/order */

import 'reflect-metadata';
import { CommonModule } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { enableProdMode, importProvidersFrom } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import '@core/model/framework/extensions/index';
import {
  bindAngularDependenciesToCoreAndMapToAngularProvider,
  coreDependenciesToAngularProviderMapper,
} from 'src/conf/di/angularProviderMapper';
import { Kernel } from '@di/kernel';
import { buildConfiguration } from 'src/conf/di/kernelConfiguration';
import { environment } from '@environments/environment';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxTagCommanderModule, WindowRef } from 'ngx-tag-commander';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { CacheInterceptor } from './app/core/interceptor/cache-interceptor';

const kernelConfiguration = buildConfiguration();
const kernel = Kernel.initialize(kernelConfiguration);

if (environment.production) {
  enableProdMode();
}

/*
  Bundles carry a hash in their file name, assets do not: the cdn in front of
  the widget caches assets/i18n/fr.json for weeks (no Cache-Control from the
  origin) and its cache key IGNORES the query string — a `?v=` buster changes
  nothing, and the cache holds one entry per exact Accept-Encoding string, so
  a stale variant can keep hitting one browser family only. The hash goes in
  the FILE NAME instead (fr.<hash>.json, copied by build/post-build.cjs): a
  path is always part of the cache key, so every deployment is a guaranteed
  miss. Left as is by ng serve, which serves the plain fr.json.
*/
const I18N_BUILD_HASH = '{{POST_BUILD_ENTERS_I18N_HASH_HERE}}';

export function HttpLoaderFactory(httpClient: HttpClient) {
  const suffix = I18N_BUILD_HASH.startsWith('{{') ? '.json' : `.${I18N_BUILD_HASH}.json`;

  return new TranslateHttpLoader(httpClient, './assets/i18n/', suffix);
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      AppRoutingModule,
      CommonModule,
      NgxTagCommanderModule,
      FormsModule,
      ReactiveFormsModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    WindowRef,
    coreDependenciesToAngularProviderMapper(kernel),
    bindAngularDependenciesToCoreAndMapToAngularProvider(kernel),
    provideHttpClient(withInterceptorsFromDi()),
  ],
}).catch((err) => console.error(err));
